/**
 * @fileoverview Plan card component for subscription display.
 * @summary Displays a single subscription plan with features and CTA.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/PlanCard
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import { getPlanBadgeLabel } from '../../../utils/planUtils'
import type { FC } from 'react'

/**
 * Props for the PlanCard component.
 */
interface PlanCardProps {
  plan: {
    type: SubscriptionType
    titleKey: string
    descriptionKey: string
    priceKey: string
    noteKey: string
    noteEngagementKey?: string
  }
  isCurrentPlan: boolean
  isLoading?: boolean
  subscriptionStatus?: 'active' | 'canceled' | 'expired'
  t: (key: string, options?: Record<string, any>) => string
  onConfigure?: () => void
  children?: React.ReactNode
}

/**
 * @summary Displays a single subscription plan card.
 * @param {PlanCardProps} props - Component props.
 * @returns {ReactElement} Plan card component.
 */
const PlanCard: FC<PlanCardProps> = ({ plan, isCurrentPlan, isLoading = false, subscriptionStatus, t, onConfigure, children }) => {
  const planLimits = SUBSCRIPTION_PLANS[plan.type]

  const getButtonLabel = () => {
    if (isCurrentPlan) {
      return t('subscription.currentPlanBadge')
    }
    return t('subscription.selectPlan')
  }

  const getButtonStyle = () => {
    if (isCurrentPlan) {
      return 'bg-gray-400 text-white cursor-default hover:bg-gray-400'
    }
    if (plan.type === SubscriptionType.PREMIUM || plan.type === SubscriptionType.ULTIMATE) {
      return 'bg-purple-600 hover:bg-purple-700 text-white'
    }
    if (plan.type === SubscriptionType.PRO) {
      return 'bg-indigo-600 hover:bg-indigo-700 text-white'
    }
    return 'bg-blue-600 hover:bg-blue-700 text-white'
  }

  return (
    <div
      className={`rounded-lg shadow-lg transition-transform duration-300 hover:shadow-xl ${
        plan.type === SubscriptionType.PREMIUM || plan.type === SubscriptionType.ULTIMATE ? 'md:scale-105 hover:scale-110 hover:shadow-2xl' : 'hover:scale-105'
      } ${isCurrentPlan ? 'ring-2 ring-blue-500 bg-white' : 'bg-white'}`}
    >
      {/* Header */}
      <div
        className={`px-6 py-8 rounded-t-lg ${
          isCurrentPlan ? 'bg-blue-600 text-white' : 'bg-linear-to-r from-gray-50 to-gray-100'
        }`}
      >
        <h3 className={`text-2xl font-bold mb-2 flex items-center gap-2 ${isCurrentPlan ? 'text-white' : 'text-gray-900'}`}>
          <span className="text-2xl">{getPlanBadgeLabel(plan.type)}</span>
          <span>{t(plan.titleKey)}</span>
        </h3>
        <p className={`text-sm ${isCurrentPlan ? 'text-blue-100' : 'text-gray-600'}`}>{t(plan.descriptionKey)}</p>
      </div>

      {/* Price */}
      <div className="px-6 py-6 border-b border-gray-200">
        <div className="text-3xl font-bold text-gray-900">
          {planLimits.priceEur ? t(plan.priceKey, { price: planLimits.priceEur }) : t(plan.priceKey)}
        </div>
      </div>

      {/* Features */}
      <div className="px-6 py-6 space-y-4">
        {/* Max Events */}
        <div className="flex items-start gap-3">
          <FontAwesomeIcon
            icon={faCheck}
            className={`mt-1 text-lg ${isCurrentPlan ? 'text-blue-600' : 'text-green-600'}`}
          />
          <div>
            <div className="font-semibold text-gray-900">{t('subscription.maxEvents')}</div>
            <div className="text-sm text-gray-600">
              {planLimits.maxEvents === Infinity
                ? t('subscription.unlimited')
                : `${planLimits.maxEvents} ${planLimits.maxEvents === 1 ? t('events.name') : t('events.events')}`}
            </div>
          </div>
        </div>

        {/* Max Songs Per Event */}
        <div className="flex items-start gap-3">
          <FontAwesomeIcon
            icon={faCheck}
            className={`mt-1 text-lg ${isCurrentPlan ? 'text-blue-600' : 'text-green-600'}`}
          />
          <div>
            <div className="font-semibold text-gray-900">{t('subscription.maxSongsPerEvent')}</div>
            <div className="text-sm text-gray-600">
              {planLimits.maxSongsPerEvent === Infinity
                ? t('subscription.unlimited')
                : `${planLimits.maxSongsPerEvent} ${planLimits.maxSongsPerEvent === 1 ? t('events.song') : t('events.songs')}`}
            
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-900">
            {t(plan.noteKey, {
              maxEvents: planLimits.maxEvents,
              maxSongsPerEvent: planLimits.maxSongsPerEvent,
            })}
          </p>
           {plan.noteEngagementKey && <p className="text-xs text-blue-900">{t(plan.noteEngagementKey)}</p>}
        </div>
      </div>

      {/* CTA Button */}
      <div className="px-6 py-6 border-t border-gray-200">
        <button
          onClick={onConfigure}
          disabled={isCurrentPlan || isLoading}
          className={`w-full py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2 ${getButtonStyle()} ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
        >
          {isLoading ? (
            <>
              <span className="inline-block animate-spin">⏳</span>
              {t('common.loading')}
            </>
          ) : (
            <>
              {getButtonLabel()}
              {!isCurrentPlan && <FontAwesomeIcon icon={faArrowRight} />}
            </>
          )}
        </button>

        {/* Cancel button for current paid plans (only if not already canceled) */}
        {isCurrentPlan && (plan.type === SubscriptionType.PRO || plan.type === SubscriptionType.PREMIUM || plan.type === SubscriptionType.ULTIMATE) && subscriptionStatus !== 'canceled' && (
          <button
            onClick={onConfigure}
            disabled={isLoading}
            className="w-full mt-2 py-2 text-sm rounded-lg font-medium transition-colors duration-200 bg-red-600 hover:bg-red-700 text-white disabled:opacity-70 disabled:cursor-wait"
          >
            {isLoading ? (
              <>
                <span className="inline-block animate-spin">⏳</span>
                <span className="ml-2">{t('common.loading')}</span>
              </>
            ) : (
              t('subscription.cancelPlan')
            )}
          </button>
        )}

        {/* Child content (ONE_SHOT configuration) */}
        {children}
      </div>
    </div>
  )
}

export default PlanCard

